import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Check, X, Lock } from 'lucide-react';
import { LOGO_DARK } from '../App';
import axios from 'axios';

import { API_URL } from '../config/api';

const API = API_URL;

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  
  const token = searchParams.get('token');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState('');

  // Password validation
  const hasMinLength = password.length >= 8;
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const isValid = hasMinLength && hasLetter && hasNumber && passwordsMatch;

  useEffect(() => {
    // Verify token on mount
    const verifyToken = async () => {
      if (!token) {
        setTokenError('Token mancante');
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/api/auth/verify-token?token=${token}`);
        setEmail(response.data.email);
        setVerifying(false);
      } catch (err) {
        setTokenError(err.response?.data?.detail || 'Token non valido o scaduto');
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/api/auth/set-password`, {
        token,
        password
      }, { withCredentials: true });

      // Save session token to localStorage as backup
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      // Set user and redirect
      setUser(response.data.user);
      navigate('/app/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#E8334A] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link non valido</h1>
          <p className="text-gray-500 mb-6">{tokenError}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 bg-[#E8334A] text-white rounded-xl font-semibold hover:bg-[#d42d42] transition-colors"
          >
            Torna al login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-6">
          <img src={LOGO_DARK} alt="Slimmy" className="h-10 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Imposta la password</h1>
          <p className="text-gray-500 mt-2">{email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Inserisci la password"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E8334A] focus:border-transparent outline-none pr-12"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="text-sm font-medium text-gray-700 mb-2">La password deve avere:</p>
            <div className="space-y-1">
              <div className={`flex items-center gap-2 text-sm ${hasMinLength ? 'text-green-600' : 'text-gray-400'}`}>
                {hasMinLength ? <Check size={16} /> : <X size={16} />}
                <span>Almeno 8 caratteri</span>
              </div>
              <div className={`flex items-center gap-2 text-sm ${hasLetter ? 'text-green-600' : 'text-gray-400'}`}>
                {hasLetter ? <Check size={16} /> : <X size={16} />}
                <span>Almeno una lettera</span>
              </div>
              <div className={`flex items-center gap-2 text-sm ${hasNumber ? 'text-green-600' : 'text-gray-400'}`}>
                {hasNumber ? <Check size={16} /> : <X size={16} />}
                <span>Almeno un numero</span>
              </div>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Conferma password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Conferma la password"
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#E8334A] focus:border-transparent outline-none ${
                  confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-gray-300'
                }`}
                data-testid="confirm-password-input"
              />
              {confirmPassword && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {passwordsMatch ? (
                    <Check size={20} className="text-green-500" />
                  ) : (
                    <X size={20} className="text-red-500" />
                  )}
                </div>
              )}
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="text-red-500 text-sm mt-1">Le password non corrispondono</p>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!isValid || loading}
            className={`w-full py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
              isValid && !loading
                ? 'bg-[#E8334A] text-white hover:bg-[#d42d42]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="set-password-btn"
          >
            {loading ? (
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
            ) : (
              <>
                <Lock size={18} />
                Completa registrazione
              </>
            )}
          </button>
        </form>

        {/* Back to login */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Hai già un account?{' '}
          <button onClick={() => navigate('/login')} className="text-[#E8334A] font-medium hover:underline">
            Accedi
          </button>
        </p>
      </div>
    </div>
  );
}
